import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, X, ArrowUpRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { usePageContent } from '@hooks/usePageContent.js';
import { CFP_DEFAULTS } from '@/content/cfpDefaults.js';
import { assetUrl } from '@/lib/assetUrl.js';

/**
 * Global floating "Submit Your Paper Now" button.
 *
 * Rendered once from the public Layout so it appears on every page. Clicking it
 * opens the SAME "Submission Guidelines" section shown on the Call for Papers
 * page (#guidelines) in an accessible popup. Content is pulled from the shared
 * admin-editable `cfp` block so the popup and the page always stay in sync.
 */
export function SubmitPaperFab() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const closeBtnRef = useRef(null);
  const openerRef = useRef(null);

  const { content: cfp } = usePageContent('cfp', CFP_DEFAULTS);
  const guidelines = cfp.guidelines ?? CFP_DEFAULTS.guidelines;
  const hideFab = pathname === '/zoom-link' || pathname === '/zoom';

  // Lock body scroll, focus the dialog, close on Escape, restore focus on close.
  useEffect(() => {
    if (!open) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);

    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 30);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
      openerRef.current?.focus?.();
    };
  }, [open]);

  if (hideFab) return null;

  return (
    <>
      {/* ── Floating action button ── */}
      <button
        ref={openerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[80] inline-flex items-center gap-2.5 rounded-full
                   bg-accent-500 pl-4 pr-5 py-3 text-ink-950 font-semibold text-sm
                   shadow-glow-accent ring-2 ring-accent-400/50
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface dark:focus-visible:ring-offset-ink-950"
      >
        <FileText size={16} className="shrink-0" />
        <span className="whitespace-nowrap">Submit Your Paper Now</span>
      </button>

      {/* ── Submission Guidelines popup (mirrors CFP #guidelines section) ── */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6 lg:p-8
                         bg-ink-950/70 backdrop-blur-sm"
              onClick={() => setOpen(false)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="submit-paper-title"
            >
              <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 24, scale: 0.98 }}
                transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full sm:max-w-6xl lg:max-w-7xl max-h-[92vh] sm:max-h-[88vh] overflow-hidden
                           rounded-t-3xl sm:rounded-3xl bg-surface dark:bg-ink-950 shadow-2xl
                           border-t border-ink-200/50 dark:border-ink-800/50
                           ring-1 ring-ink-200/70 dark:ring-ink-800/70 flex flex-col"
              >
                <button
                  ref={closeBtnRef}
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="absolute top-5 right-5 sm:top-6 sm:right-6 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full
                             bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <X size={18} />
                </button>

                {/* Same layout as CFP #guidelines */}
                <div className="flex-1 overflow-y-auto px-6 sm:px-8 lg:px-10 py-10 sm:py-12 lg:py-14">
                  <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <div className="lg:col-span-7">
                      <p className="eyebrow mb-3">{guidelines.eyebrow || 'Submission Guidelines'}</p>
                      <h2 id="submit-paper-title" className="h-display text-3xl sm:text-4xl lg:text-5xl text-balance mb-6 pr-10 text-ink-900 dark:text-ink-50">
                        {guidelines.title}
                      </h2>
                      <div className="prose prose-ink dark:prose-invert max-w-none space-y-4 text-ink-600 dark:text-ink-300">
                        {(guidelines.paragraphs ?? []).map((p, i) => (
                          <p key={i}>{p}</p>
                        ))}
                      </div>
                      <div className="mt-8 flex flex-wrap gap-3">
                        {guidelines.primaryLabel && (
                          <a
                            href={guidelines.primaryUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary"
                          >
                            {guidelines.primaryLabel} <ArrowUpRight size={16} />
                          </a>
                        )}
                        {guidelines.secondaryLabel && (
                          <a
                            href={guidelines.secondaryUrl}
                            onClick={() => setOpen(false)}
                            className="btn-ghost"
                          >
                            {guidelines.secondaryLabel}
                          </a>
                        )}
                      </div>
                    </div>

                    {guidelines.logo && (
                      <div className="lg:col-span-5 flex justify-center lg:justify-end">
                        <a
                          href={guidelines.primaryUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full max-w-sm rounded-3xl bg-white shadow-soft border border-ink-200/60 p-6 sm:p-8 hover:shadow-glow transition-shadow"
                        >
                          <img
                            src={assetUrl(guidelines.logo)}
                            alt={guidelines.logoAlt || ''}
                            className="w-full h-auto object-contain"
                          />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
